import { SPECIAL_KEYCODES } from '../core/protocol';
import { appState, farmScreenOff, deviceAliases, commentCache } from '../core/state';
import { can } from '../core/access';
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { showDeviceInfoModal, showDeviceCommentModal } from '../devices/device-modals';
import { showClickerRestartModal } from '../clicker/clicker-restart-modal';
import { canStopClicker, confirmClickerStop, stopBlockedReason } from '../clicker/clicker-shared';
import { openClickerLogWindow } from '../clicker/clicker-info-modal';
import { openClickerOperationsWindow } from '../clicker/clicker-operations-modal';
import { openClickerHistoryWindow } from '../clicker/clicker-history-modal';
import { openCloseShiftModal } from '../clicker/close-shift-modal';
import { closeShiftBtnHtml as sharedCloseShiftBtnHtml } from '../clicker/close-shift-shared';
import { resolveFarmRecordById } from '../farms/farms-view';
import { StreamSession } from './stream-session';
import { connectIconSvg, disconnectIconSvg, lockIconSvg, eyeIconSvg, clickerStartSvg, clickerStopSvg, clickerSettingsSvg, clickerLogSvg, clickerRegistrySvg, clickerHistorySvg, infoSvg, commentSvg, screenshotSvg, refreshIconSvg, flashControl, deviceGlyphSvg, shareLinkSvg, } from './stream-controls-shared';
import { showDeviceShareModal } from '../devices/device-share-modal';
import { FarmType } from '../core/farm-type';
// Re-export the two glyphs other modules still import from here (device-card.ts uses
// connectIconSvg); the canonical definitions live in stream-controls-shared.ts.
export { connectIconSvg, eyeIconSvg };
export const inlineStreams = new Map();
// Last one-shot ADB screen grab per video-less device, kept across card re-renders.
// The card is rebuilt on every clicker start/stop (and on each snapshot poll), which
// would otherwise reset the shown screen back to the "Screen not loaded" placeholder;
// caching the blob lets renderAdbControls repaint it. Cleared when the device drops.
const adbScreens = new Map();
// Timestamp of the last screen fetch per device — throttles Connect/Refresh so a
// screencap runs at most once a second, no matter how fast the button is clicked.
const adbScreenLoadAt = new Map();
const ADB_SCREEN_MIN_MS = 1000;
export function deviceIcon(d) {
    return deviceGlyphSvg(d.deviceKind);
}
// Display name used by the side-panel actions (alias → model → udid), matching
// the device card's own resolution.
function deviceDisplayName(d) {
    return deviceAliases.get(d.udid) || d['ro.product.model'] || d.udid;
}
// «Close shift» — launched from the device card only. Hidden until wireClickerControls
// learns the farm's team has it enabled (teams.close_shift).
function closeShiftBtnHtml() {
    return sharedCloseShiftBtnHtml('stream-clicker-shift');
}
/**
 * The control column beside a live stream.
 *
 * `canControl` false = the picture is one-way (a phone client's screen-grab stream has
 * no control socket to send a tap back through). Everything that drives the device — power,
 * clipboard, the nav keys — is then left OUT rather than rendered disabled: a permanent
 * capability gap is not a state the operator can wait out, and a row of dead buttons
 * reads as "broken" instead of "not applicable". What stays works either way.
 */
export function streamControlsHtml(canControl = true) {
    return `
        <div class="device-stream-controls" aria-label="${t('stream.device_navigation')}">
            ${canControl && can('device.power')
        ? `<button class="stream-control-btn" data-keycode="26" title="${t('stream.power')}">
                <svg viewBox="0 0 24 24"><path d="M12 2v9M6.3 5.8a8 8 0 1 0 11.4 0"/></svg>
            </button>`
        : ''}
            ${canControl ? `
            <button class="stream-control-btn stream-clipboard-copy" title="${t('stream.copy_device_clipboard')}">
                <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            </button>
            <button class="stream-control-btn stream-clipboard-paste" title="${t('stream.paste_clipboard_to_device')}">
                <svg viewBox="0 0 24 24"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M12 11v6M9 14l3 3 3-3"/></svg>
            </button>` : ''}
            <button class="stream-control-btn stream-screenshot" title="${t('stream.screenshot_to_clipboard')}">${screenshotSvg}</button>
            <button class="stream-control-btn stream-info-btn" title="${t('stream.device_info')}" aria-label="${t('stream.device_info')}">${infoSvg}</button>
            <button class="stream-control-btn stream-comment-btn" title="${t('stream.comment')}" aria-label="${t('stream.comment')}">${commentSvg}</button>
            <span class="stream-controls__divider stream-clicker-divider" style="display:none"></span>
            <button type="button" class="stream-control-btn stream-clicker-toggle" title="${t('stream.clicker')}" aria-label="${t('stream.clicker')}" disabled style="display:none">${clickerStartSvg}</button>
            <button type="button" class="stream-control-btn stream-clicker-settings" title="${t('stream.reconfigure_restart_clicker')}" aria-label="${t('stream.reconfigure_clicker')}" disabled style="display:none">${clickerSettingsSvg}</button>
            ${can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn stream-clicker-logs" title="${t('stream.clicker_logs')}" aria-label="${t('stream.clicker_logs')}" disabled style="display:none">${clickerLogSvg}</button>`
        : ''}
            <button type="button" class="stream-control-btn stream-clicker-ops" title="${t('stream.operations_registry')}" aria-label="${t('stream.operations_registry')}" disabled style="display:none">${clickerRegistrySvg}</button>
            ${can('device.clickerHistory')
        ? `<button type="button" class="stream-control-btn stream-clicker-history" title="${t('stream.clicker_history')}" aria-label="${t('stream.clicker_history')}" disabled style="display:none">${clickerHistorySvg}</button>`
        : ''}
            ${closeShiftBtnHtml()}
            <span class="stream-controls__divider stream-share-divider" style="display:none"></span>
            <button type="button" class="stream-control-btn stream-share-btn" title="${t('stream.share_links')}" aria-label="${t('stream.share_links')}" style="display:none">${shareLinkSvg}</button>
            <span class="stream-controls__spacer"></span>
            ${canControl ? `
            <button class="stream-control-btn" data-keycode="4" title="${t('common.back')}">
                <svg viewBox="0 0 24 24"><path d="m15 5-7 7 7 7"/></svg>
            </button>
            <button class="stream-control-btn" data-keycode="3" title="${t('stream.home')}">
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="7"/></svg>
            </button>
            <button class="stream-control-btn" data-keycode="187" title="${t('stream.recent_apps')}">
                <svg viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="14" rx="1.5"/></svg>
            </button>` : ''}
        </div>
    `;
}
// Reduced control column for a device with no live scrcpy session — either online but
// not connected yet, or offline. Only the session-free actions are offered: info,
// comment, the clicker group, and (online only) a one-shot ADB screencap. Power /
// clipboard / nav all ride the live control channel, so they're omitted until Connect
// opens the stream and streamControlsHtml() takes over.
//
// `offline` drops what needs a reachable phone — the screencap, Start/Stop and the
// reconfigure gear — and keeps what the panel/agent can answer on its own: info from
// the last reported state, the clicker's own log, the run history and the registry.
export function idleControlsHtml(offline = false) {
    return `
        <div class="device-stream-controls" aria-label="${t('stream.device_controls')}">
            ${offline ? '' : `<button type="button" class="stream-control-btn idle-screenshot" title="${t('stream.screenshot_to_clipboard')}" aria-label="${t('stream.screenshot')}">${screenshotSvg}</button>`}
            <button type="button" class="stream-control-btn stream-info-btn" title="${offline ? t('stream.device_info_last_known') : t('stream.device_info')}" aria-label="${t('stream.device_info')}">${infoSvg}</button>
            <button type="button" class="stream-control-btn stream-comment-btn" title="${t('stream.comment')}" aria-label="${t('stream.comment')}">${commentSvg}</button>
            <span class="stream-controls__divider stream-clicker-divider" style="display:none"></span>
            ${offline ? '' : `<button type="button" class="stream-control-btn stream-clicker-toggle" title="${t('stream.clicker')}" aria-label="${t('stream.clicker')}" disabled style="display:none">${clickerStartSvg}</button>
            <button type="button" class="stream-control-btn stream-clicker-settings" title="${t('stream.reconfigure_restart_clicker')}" aria-label="${t('stream.reconfigure_clicker')}" disabled style="display:none">${clickerSettingsSvg}</button>`}
            ${can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn stream-clicker-logs" title="${t('stream.clicker_logs')}" aria-label="${t('stream.clicker_logs')}" disabled style="display:none">${clickerLogSvg}</button>`
        : ''}
            <button type="button" class="stream-control-btn stream-clicker-ops" title="${t('stream.operations_registry')}" aria-label="${t('stream.operations_registry')}" disabled style="display:none">${clickerRegistrySvg}</button>
            ${can('device.clickerHistory')
        ? `<button type="button" class="stream-control-btn stream-clicker-history" title="${t('stream.clicker_history')}" aria-label="${t('stream.clicker_history')}" disabled style="display:none">${clickerHistorySvg}</button>`
        : ''}
            ${closeShiftBtnHtml()}
            ${offline ? '' : `<span class="stream-controls__divider stream-share-divider" style="display:none"></span>
            <button type="button" class="stream-control-btn stream-share-btn" title="${t('stream.share_links')}" aria-label="${t('stream.share_links')}" style="display:none">${shareLinkSvg}</button>`}
        </div>
    `;
}
export function stopInlineStream(udid) {
    const stream = inlineStreams.get(udid);
    if (!stream)
        return;
    stream.session.close();
    inlineStreams.delete(udid);
}
export async function kickStream(udid) {
    await fetch(`/api/devices/${encodeURIComponent(udid)}/stream/kick`, { method: 'DELETE' });
}
export function startInlineStream(d, card) {
    stopInlineStream(d.udid);
    const canvas = document.createElement('canvas');
    canvas.className = 'device-stream-canvas';
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const stream = {
        canvas,
        session: undefined,
    };
    // Optionally blank the device's physical screen as soon as the stream is up,
    // when the device's farm has the "screen off on connect" preference enabled.
    // The control channel is open by the time metadata arrives; fire it once.
    const autoScreenOff = !!d.farmName && farmScreenOff.get(d.farmName) === true;
    let autoScreenOffDone = false;
    stream.session = new StreamSession(d.udid, canvas, ctx, (meta) => {
        stream.metadata = meta;
        if (autoScreenOff && !autoScreenOffDone) {
            autoScreenOffDone = true;
            stream.session.sendScreenPower(false);
        }
    }, () => { stream.metadata = undefined; }, (by) => {
        stopInlineStream(d.udid);
        d.takenByLogin = by;
        renderInlinePlayer(card, d);
    });
    inlineStreams.set(d.udid, stream);
    renderInlinePlayer(card, d);
}
/**
 * Open a read-only co-view of the device (admin+). Watches the screen via the
 * panel-side fan-out without taking control or evicting the operator: connects to
 * ?action=watch, attaches no input handlers, sends no control. Decode is identical.
 */
export function startWatchStream(d, card) {
    stopInlineStream(d.udid);
    const canvas = document.createElement('canvas');
    canvas.className = 'device-stream-canvas';
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const stream = {
        canvas,
        readOnly: true,
        session: undefined,
    };
    stream.session = new StreamSession(d.udid, canvas, ctx, (meta) => { stream.metadata = meta; }, () => { stream.metadata = undefined; }, undefined, true);
    inlineStreams.set(d.udid, stream);
    renderInlinePlayer(card, d);
}
export function renderInlinePlayer(card, d) {
    const player = card.querySelector('.device-card__player');
    const connectButton = card.querySelector('.stream-btn');
    const stream = inlineStreams.get(d.udid);
    player.innerHTML = '';
    // A device held by *us* (a stale own session) is reclaimable — connecting
    // self-takes it over server-side — so don't treat it as locked.
    const takenByOther = d.takenByLogin && !stream && d.takenByLogin !== appState.currentUserLogin;
    if (!stream || d.state !== 'device') {
        if (stream)
            stopInlineStream(d.udid);
        if (takenByOther) {
            const kickBtn = can('stream.kick')
                ? `<button class="inuse-btn inuse-btn--danger kick-btn" type="button">${disconnectIconSvg}<span>${t('stream.force_disconnect')}</span></button>`
                : '';
            const watchBtn = can('device.watch')
                ? `<button class="inuse-btn watch-btn" type="button">${eyeIconSvg}<span>${t('stream.watch')}</span></button>`
                : '';
            const actions = (kickBtn || watchBtn)
                ? `<div class="inuse-actions">${watchBtn}${kickBtn}</div>`
                : '';
            // Someone else holds the scrcpy session, but the right-hand control column
            // stays: clicker (start/stop/reconfigure/logs), info, comment and screenshot
            // are all REST and need no live session, so they must stay reachable exactly
            // like a normal/idle card. Only the "In use by … + Watch/Force disconnect"
            // placeholder replaces the video area.
            const stage = document.createElement('div');
            stage.className = 'device-stream-stage';
            const canvasWrap = document.createElement('div');
            canvasWrap.className = 'device-stream-canvas-wrap';
            canvasWrap.innerHTML = `
                <div class="device-player-placeholder">
                    <span>${lockIconSvg}</span>
                    <strong>${t('stream.in_use_by', { login: esc(d.takenByLogin) })}</strong>
                    ${actions}
                </div>`;
            stage.appendChild(canvasWrap);
            stage.insertAdjacentHTML('beforeend', idleControlsHtml());
            player.appendChild(stage);
            if (can('stream.kick')) {
                player.querySelector('.kick-btn')?.addEventListener('click', () => {
                    if (confirm(t('stream.confirm_disconnect_user', { login: d.takenByLogin })))
                        void kickStream(d.udid);
                });
            }
            if (can('device.watch')) {
                player.querySelector('.watch-btn')?.addEventListener('click', () => startWatchStream(d, card));
            }
            // Wire the reduced control column (same set the idle card offers). noVideo=false
            // so a clicker start/stop re-renders through renderInlinePlayer — which lands
            // back here (still no stream) and repaints the column with the new run state.
            const displayName = deviceDisplayName(d);
            wireInfoComment(player, d, displayName, true);
            wireClickerControls(player, d, displayName, false);
            wireShareControl(player, d, displayName);
            wireIdleScreenshot(player.querySelector('.idle-screenshot'), d);
            connectButton.innerHTML = lockIconSvg;
            connectButton.title = t('stream.in_use_by', { login: d.takenByLogin });
            connectButton.setAttribute('aria-label', t('stream.in_use'));
            connectButton.disabled = true;
        }
        else if (d.state === 'device') {
            // Online but not connected: show the idle placeholder AND a reduced control
            // column so the clicker/info/comment/screenshot stay reachable without
            // opening the stream. Power/clipboard/nav need the live control channel, so
            // they're omitted here and return once Connect opens the stream.
            const stage = document.createElement('div');
            stage.className = 'device-stream-stage';
            const canvasWrap = document.createElement('div');
            canvasWrap.className = 'device-stream-canvas-wrap';
            canvasWrap.innerHTML = `
                <div class="device-player-placeholder">
                    <span>${deviceIcon(d)}</span>
                    <strong>${t('stream.not_connected')}</strong>
                </div>`;
            stage.appendChild(canvasWrap);
            stage.insertAdjacentHTML('beforeend', idleControlsHtml());
            player.appendChild(stage);
            const displayName = deviceDisplayName(d);
            wireInfoComment(player, d, displayName, true);
            // noVideo=false so a clicker start/stop re-renders through renderInlinePlayer
            // — which lands back in this same idle branch (still no stream) and repaints
            // the reduced column with the new run state.
            wireClickerControls(player, d, displayName, false);
            wireShareControl(player, d, displayName);
            wireIdleScreenshot(player.querySelector('.idle-screenshot'), d);
            connectButton.innerHTML = connectIconSvg;
            connectButton.title = t('stream.connect');
            connectButton.setAttribute('aria-label', t('stream.connect'));
            connectButton.disabled = false;
        }
        else {
            // Offline: no picture and no adb, but everything the panel and the farm agent
            // hold on their own stays reachable — the last reported device info, the
            // clicker's log backlog, its run history and the operations registry.
            renderOfflineStage(player, d);
            connectButton.innerHTML = connectIconSvg;
            connectButton.title = t('stream.connect');
            connectButton.setAttribute('aria-label', t('stream.connect'));
            connectButton.disabled = true;
        }
        return;
    }
    const stage = document.createElement('div');
    stage.className = 'device-stream-stage';
    stage.tabIndex = 0; // focusable so Ctrl+C / Ctrl+V land on this stream
    const canvasWrap = document.createElement('div');
    canvasWrap.className = 'device-stream-canvas-wrap';
    canvasWrap.appendChild(stream.canvas);
    stage.appendChild(canvasWrap);
    // Read-only co-view: show a badge, skip the control bar and all input wiring.
    // The session itself ignores any control too (defense-in-depth), and the server
    // never forwards a watcher's messages to the farm.
    if (stream.readOnly) {
        // View-only: signal "no control" with a not-allowed cursor over the video.
        stage.classList.add('device-stream-stage--readonly');
        canvasWrap.insertAdjacentHTML('beforeend', `<span class="stream-watch-badge stream-watch-badge--watching">${eyeIconSvg}<span>${t('stream.watching_readonly')}</span></span>`);
        // The right control column stays even while watching: screenshot, info,
        // comment and the clicker group are all REST (no control channel), so a
        // read-only co-viewer keeps them exactly like the in-use placeholder above.
        // Only the device-input path (power/clipboard/nav) is withheld — a watcher
        // sends no control — so the reduced idle column is the right set here.
        stage.insertAdjacentHTML('beforeend', idleControlsHtml());
        player.appendChild(stage);
        const displayName = deviceDisplayName(d);
        wireInfoComment(player, d, displayName, true);
        // noVideo=false so a clicker start/stop re-renders through renderInlinePlayer
        // — the read-only stream is still live, so it lands back in this branch and
        // repaints the column with the new run state.
        wireClickerControls(player, d, displayName, false);
        wireShareControl(player, d, displayName);
        wireIdleScreenshot(player.querySelector('.idle-screenshot'), d);
        connectButton.innerHTML = disconnectIconSvg;
        connectButton.title = t('stream.stop_watching');
        connectButton.setAttribute('aria-label', t('stream.stop_watching'));
        connectButton.disabled = false;
        return;
    }
    // Operator-facing "someone is watching me" indicator: shown only while ≥1
    // read-only co-viewer is connected to this device (relay-reported watcherCount).
    // Distinct from the watcher's own stream.readOnly self-badge above.
    if ((d.watcherCount ?? 0) > 0) {
        const label = d.watcherCount > 1 ? t('stream.watched_count', { count: d.watcherCount }) : t('stream.watched');
        canvasWrap.insertAdjacentHTML('beforeend', `<span class="stream-watch-badge stream-watch-badge--observed" title="${t('stream.watchers_title', { count: d.watcherCount })}">${eyeIconSvg}<span>${esc(label)}</span></span>`);
    }
    // Read-only lock while the device's clicker is running: no human control leaks
    // to the farm (the relay + device-command endpoint enforce this server-side; here
    // we mirror it in the UI). Device-driving buttons are disabled and the touch/key
    // path is muted via the session lock; the clicker/reconfigure/info/comment/
    // screenshot actions stay live so the operator can still stop or inspect it.
    // A phone client's picture is a stream of screen grabs, which is one-way — there is
    // no control socket to send a tap back through. That lock is permanent, not a run-state,
    // so it is reported by the device rather than derived here.
    // Only a superadmin keeps control while the clicker runs ('stream.clickerBypass');
    // everyone else, admins included, watches. The permanent one-way lock (noControl, a
    // phone client with no control socket) is a hardware fact and always applies.
    const noControl = d['stream.control'] === false;
    const clickerLock = noControl || (!!d.clickerRunning && !can('stream.clickerBypass'));
    stream.session.setLocked(clickerLock);
    if (clickerLock) {
        // Control is muted — cursor says so.
        stage.classList.add('device-stream-stage--readonly');
        const label = noControl ? t('stream.view_only') : t('stream.readonly_clicker_running');
        canvasWrap.insertAdjacentHTML('beforeend', `<span class="stream-watch-badge stream-watch-badge--locked">${lockIconSvg}<span>${label}</span></span>`);
    }
    stage.insertAdjacentHTML('beforeend', streamControlsHtml(!noControl));
    player.appendChild(stage);
    player.querySelectorAll('.stream-control-btn').forEach((button) => {
        button.addEventListener('click', () => {
            const keycode = Number(button.dataset['keycode']);
            if (Number.isInteger(keycode))
                stream.session.sendKeycode(keycode);
        });
    });
    // Disable the device-driving controls (power, clipboard copy/paste, nav keys)
    // while locked; the clicker/reconfigure/info/comment/screenshot buttons remain.
    if (clickerLock) {
        stage.querySelectorAll('.stream-control-btn[data-keycode], .stream-clipboard-copy, .stream-clipboard-paste')
            .forEach((b) => { b.disabled = true; });
    }
    // Type the host clipboard onto the device (scrcpy SET path is unreliable on
    // modern Android, so we inject the text via `input text` on the relay).
    const sendTextToDevice = (text) => {
        if (!text)
            return;
        void fetch(`/api/devices/${encodeURIComponent(d.udid)}/command`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ command: 'input-text', text }),
        }).catch(() => { });
    };
    // Forward the physical keyboard to the device while the stream is focused.
    // The stage grabs focus on click (canvas suppresses default focus).
    stage.addEventListener('mousedown', () => stage.focus());
    stage.addEventListener('keydown', (e) => {
        if (clickerLock)
            return; // read-only: keyboard muted while the clicker runs
        const special = SPECIAL_KEYCODES[e.key];
        if (special !== undefined) {
            e.preventDefault();
            stream.session.sendKeycode(special);
        }
        else if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            e.preventDefault();
            stream.session.sendText(e.key);
        }
    });
    player.querySelector('.stream-clipboard-paste')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        let text;
        try {
            text = await navigator.clipboard.readText();
        }
        catch {
            return;
        }
        if (!text)
            return;
        btn.disabled = true;
        sendTextToDevice(text);
        btn.disabled = false;
    });
    // Copy the device clipboard onto the host clipboard. The farm pushes new
    // clipboard values over the stream WS; we light the button (.has-update) when
    // a fresh value arrives and write it on click (a user gesture, which Chrome
    // requires for clipboard writes). The badge clears once copied.
    const copyBtn = player.querySelector('.stream-clipboard-copy');
    if (copyBtn) {
        stream.session.onClipboardUpdate = () => copyBtn.classList.add('has-update');
        copyBtn.addEventListener('click', async () => {
            copyBtn.classList.remove('has-update');
            // Read on click: ask the device for its current clipboard and use the
            // fresh value, falling back to the cached one if it doesn't answer in
            // time. This removes the wait on scrcpy's (laggy) autosync push.
            const text = await stream.session.requestClipboard();
            if (!text)
                return;
            try {
                await navigator.clipboard.writeText(text);
            }
            catch {
                return;
            }
            copyBtn.classList.add('just-copied');
            setTimeout(() => copyBtn.classList.remove('just-copied'), 900);
        });
    }
    const displayName = deviceDisplayName(d);
    wireInfoComment(player, d, displayName);
    wireClickerControls(player, d, displayName, false);
    wireShareControl(player, d, displayName);
    wireScreenshot(player.querySelector('.stream-screenshot'), stream);
    connectButton.innerHTML = disconnectIconSvg;
    connectButton.title = t('stream.disconnect');
    connectButton.setAttribute('aria-label', t('stream.disconnect'));
    connectButton.disabled = false;
}
/**
 * The offline device panel: the "Device offline" placeholder plus the reduced control
 * column in its offline flavour. Shared with the aggregator farm's offline card
 * (farms/aggregator-devices.ts), which has no live descriptor and builds one from the
 * last state the phone reported.
 */
export function renderOfflineStage(player, d) {
    const stage = document.createElement('div');
    stage.className = 'device-stream-stage';
    const canvasWrap = document.createElement('div');
    canvasWrap.className = 'device-stream-canvas-wrap';
    canvasWrap.innerHTML = `
        <div class="device-player-placeholder">
            <span>${deviceIcon(d)}</span>
            <strong>${t('stream.device_offline')}</strong>
        </div>`;
    stage.appendChild(canvasWrap);
    stage.insertAdjacentHTML('beforeend', idleControlsHtml(true));
    player.appendChild(stage);
    const displayName = deviceDisplayName(d);
    wireInfoComment(player, d, displayName, true);
    wireClickerControls(player, d, displayName, true, true);
}
// Info + comment buttons — session-free (REST/modals), so they're wired both from
// the live stream panel and from the video-less adb/clicker panel below.
function wireInfoComment(player, d, displayName, noVideo = false) {
    // Device info — same modal the card used to open from its top toolbar.
    player.querySelector('.stream-info-btn')
        ?.addEventListener('click', () => showDeviceInfoModal(d, displayName, noVideo));
    // Comment — seed the modal from cache when available, otherwise fetch first.
    player.querySelector('.stream-comment-btn')?.addEventListener('click', () => {
        const cached = commentCache.get(d.udid);
        if (cached !== undefined) {
            showDeviceCommentModal(d.udid, displayName, cached);
            return;
        }
        void fetch(`/api/devices/${encodeURIComponent(d.udid)}/comment`)
            .then((r) => (r.ok ? r.json() : { comment: '' }))
            .then((data) => {
            const c = data.comment ?? '';
            commentCache.set(d.udid, c);
            showDeviceCommentModal(d.udid, displayName, c);
        })
            .catch(() => showDeviceCommentModal(d.udid, displayName, ''));
    });
}
/**
 * Video-less card body for devices whose farm can't stream (aggregator / phone
 * clients — adb-only). No scrcpy connect: just the clicker controls (Start/Stop,
 * reconfigure, logs) plus info/comment, all of which drive REST endpoints and need
 * no live session. Reuses the stream stage/controls layout minus the canvas.
 */
export function renderAdbControls(card, d) {
    const player = card.querySelector('.device-card__player');
    const online = d.state === 'device';
    const displayName = deviceDisplayName(d);
    // Clicker drives the device: no human input while it holds the read-only lock —
    // except for a superadmin ('stream.clickerBypass'), the only role that keeps control
    // while the clicker runs.
    const readOnly = !!d.clickerRunning && !can('stream.clickerBypass');
    const clickerLogBtn = can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn stream-clicker-logs" title="${t('stream.clicker_logs')}" aria-label="${t('stream.clicker_logs')}" disabled style="display:none">${clickerLogSvg}</button>`
        : '';
    const clickerOpsBtn = `<button type="button" class="stream-control-btn stream-clicker-ops" title="${t('stream.operations_registry')}" aria-label="${t('stream.operations_registry')}" disabled style="display:none">${clickerRegistrySvg}</button>`;
    const clickerHistoryBtn = can('device.clickerHistory')
        ? `<button type="button" class="stream-control-btn stream-clicker-history" title="${t('stream.clicker_history')}" aria-label="${t('stream.clicker_history')}" disabled style="display:none">${clickerHistorySvg}</button>`
        : '';
    // Navigation + screenshot ride ADB (device-command / screencap query), not a
    // scrcpy control stream — a video-less device has none. Nav keys are device
    // control, so they're disabled while the clicker holds the device read-only.
    const navControls = online ? `
                <button type="button" class="stream-control-btn adb-nav" data-navcmd="nav-back" title="${t('common.back')}" aria-label="${t('common.back')}" ${readOnly ? 'disabled' : ''}>
                    <svg viewBox="0 0 24 24"><path d="m15 5-7 7 7 7"/></svg>
                </button>
                <button type="button" class="stream-control-btn adb-nav" data-navcmd="nav-home" title="${t('stream.home')}" aria-label="${t('stream.home')}" ${readOnly ? 'disabled' : ''}>
                    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="7"/></svg>
                </button>
                <button type="button" class="stream-control-btn adb-nav" data-navcmd="nav-recent" title="${t('stream.recent_apps')}" aria-label="${t('stream.recent_apps')}" ${readOnly ? 'disabled' : ''}>
                    <svg viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="14" rx="1.5"/></svg>
                </button>
                <button type="button" class="stream-control-btn adb-screenshot-dl" title="${t('stream.download_screenshot')}" aria-label="${t('stream.download_screenshot')}">${screenshotSvg}</button>
                <span class="stream-controls__divider"></span>` : '';
    player.innerHTML = `
        <div class="device-stream-stage">
            <div class="device-stream-canvas-wrap adb-screen-wrap">
                <div class="device-player-placeholder adb-screen-placeholder">
                    <span>${deviceIcon(d)}</span>
                    <strong>${online ? t('stream.screen_not_loaded') : t('stream.device_offline')}</strong>
                    ${online ? `<button type="button" class="adb-connect-btn">${connectIconSvg}<span>${t('stream.connect')}</span></button>` : ''}
                </div>
            </div>
            <div class="device-stream-controls" aria-label="${t('stream.device_controls')}">
                ${navControls}
                <button type="button" class="stream-control-btn stream-info-btn" title="${t('stream.device_info')}" aria-label="${t('stream.device_info')}">${infoSvg}</button>
                <button type="button" class="stream-control-btn stream-comment-btn" title="${t('stream.comment')}" aria-label="${t('stream.comment')}">${commentSvg}</button>
                <span class="stream-controls__divider stream-clicker-divider" style="display:none"></span>
                <button type="button" class="stream-control-btn stream-clicker-toggle" title="${t('stream.clicker')}" aria-label="${t('stream.clicker')}" disabled style="display:none">${clickerStartSvg}</button>
                <button type="button" class="stream-control-btn stream-clicker-settings" title="${t('stream.reconfigure_restart_clicker')}" aria-label="${t('stream.reconfigure_clicker')}" disabled style="display:none">${clickerSettingsSvg}</button>
                ${clickerLogBtn}
                ${clickerOpsBtn}
                ${clickerHistoryBtn}
                ${closeShiftBtnHtml()}
            </div>
        </div>
    `;
    wireInfoComment(player, d, displayName, true);
    if (online) {
        wireAdbScreen(player, d);
        wireClickerControls(player, d, displayName, true);
    }
    else {
        // Offline: drop any cached grab so it doesn't reappear stale on reconnect.
        adbScreens.delete(d.udid);
        adbScreenLoadAt.delete(d.udid);
    }
}
// Drives the video-less card's one-shot screen view: Connect / Refresh fetch a fresh
// `screencap` PNG (via the /screenshot device-query), the nav buttons send ADB
// keyevents, and the screenshot button downloads the current grab. All REST — no
// live session, so nothing to keep open or tear down.
function wireAdbScreen(player, d) {
    const wrap = player.querySelector('.adb-screen-wrap');
    if (!wrap)
        return;
    const url = `/api/devices/${encodeURIComponent(d.udid)}/screenshot`;
    let objectUrl = null;
    const showError = (msg) => {
        const strong = wrap.querySelector('.adb-screen-placeholder strong');
        if (strong)
            strong.textContent = msg;
    };
    // Paint a grab into the wrap (img + refresh button) and cache it, so a later card
    // rebuild (clicker start/stop, snapshot poll) re-shows it instead of resetting to
    // the "Screen not loaded" placeholder.
    const paint = (blob) => {
        adbScreens.set(d.udid, blob);
        if (objectUrl)
            URL.revokeObjectURL(objectUrl);
        objectUrl = URL.createObjectURL(blob);
        wrap.innerHTML = `
            <img class="device-stream-canvas adb-screenshot" src="${objectUrl}" alt="${t('stream.device_screen')}">
            <button type="button" class="stream-control-btn adb-refresh-btn" title="${t('stream.refresh_screen')}" aria-label="${t('stream.refresh_screen')}">${refreshIconSvg}</button>
        `;
        const refreshBtn = wrap.querySelector('.adb-refresh-btn');
        refreshBtn.addEventListener('click', (e) => { void loadScreen(e.currentTarget); });
        // Keep Refresh disabled until the 1s throttle window since the last grab elapses.
        const since = Date.now() - (adbScreenLoadAt.get(d.udid) ?? 0);
        if (since < ADB_SCREEN_MIN_MS) {
            refreshBtn.disabled = true;
            setTimeout(() => { refreshBtn.disabled = false; }, ADB_SCREEN_MIN_MS - since);
        }
    };
    // Fetch a fresh grab and paint it; returns the blob (null on fail). Throttled to
    // at most one screencap per second per device.
    const loadScreen = async (trigger) => {
        const wait = ADB_SCREEN_MIN_MS - (Date.now() - (adbScreenLoadAt.get(d.udid) ?? 0));
        if (wait > 0)
            return null;
        adbScreenLoadAt.set(d.udid, Date.now());
        trigger.disabled = true;
        try {
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                showError(data.error ?? t('stream.screenshot_failed'));
                return null;
            }
            const blob = await res.blob();
            paint(blob);
            return blob;
        }
        catch {
            showError(t('stream.network_error'));
            return null;
        }
        finally {
            trigger.disabled = false;
        }
    };
    player.querySelector('.adb-connect-btn')
        ?.addEventListener('click', (e) => { void loadScreen(e.currentTarget); });
    // Re-show the last grab across card rebuilds so the screen doesn't reset when the
    // clicker is started/stopped or a new snapshot arrives.
    const cached = adbScreens.get(d.udid);
    if (cached)
        paint(cached);
    // Nav keys → device-command; brief flash on the button (red on 409 read-only etc.).
    player.querySelectorAll('.adb-nav').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const command = btn.dataset['navcmd'];
            if (!command)
                return;
            btn.disabled = true;
            try {
                const res = await fetch(`/api/devices/${encodeURIComponent(d.udid)}/command`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ command }),
                });
                flashControl(btn, res.ok ? 'flash-ok' : 'flash-err');
            }
            catch {
                flashControl(btn, 'flash-err');
            }
            finally {
                btn.disabled = false;
            }
        });
    });
    // Screenshot button downloads the current grab (fetching one first if none loaded).
    player.querySelector('.adb-screenshot-dl')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        const blob = adbScreens.get(d.udid) ?? await loadScreen(btn);
        if (!blob) {
            flashControl(btn, 'flash-err');
            return;
        }
        const dlUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dlUrl;
        a.download = `${deviceDisplayName(d)}.png`;
        a.click();
        URL.revokeObjectURL(dlUrl);
        flashControl(btn, 'flash-ok');
    });
}
// Clicker controls shown as a separated group below the meta buttons: Start/Stop,
// Reconfigure (always available), and — for superadmins only — a log viewer. Run
// state is authoritative from the device broadcast (`d.clickerRunning`); the card is
// rebuilt on every start/stop transition, and we also re-render it optimistically on
// click so the read-only lock/badge appear at once instead of a round-trip later.
// The farm id (needed by the per-farm clicker endpoints, server-side authorized) is
// resolved once in the background and awaited in the handlers, so the icons appear
// immediately rather than after a fetch. No extra UI gate on Start/Stop/Reconfigure —
// they lean on server authorization; the log button is superadmin-gated in markup.
function wireClickerControls(player, d, displayName, noVideo, offline = false) {
    const toggleBtn = player.querySelector('.stream-clicker-toggle');
    const gearBtn = player.querySelector('.stream-clicker-settings');
    const logsBtn = player.querySelector('.stream-clicker-logs'); // superadmin-only; absent otherwise
    const opsBtn = player.querySelector('.stream-clicker-ops'); // registry — visible to everyone
    const historyBtn = player.querySelector('.stream-clicker-history'); // run audit — admin-only; absent otherwise
    const shiftBtn = player.querySelector('.stream-clicker-shift'); // close shift — absent without 'clicker.closeShift'
    const divider = player.querySelector('.stream-clicker-divider');
    if (!d.farmName)
        return; // no farm → the device can't drive a clicker
    // Offline: Start/Stop and the reconfigure gear aren't rendered at all (they need a
    // reachable phone), so only the read-only group below is wired.
    if (!offline && !toggleBtn)
        return;
    const running = !!d.clickerRunning;
    // Told to stop, farm has not confirmed: the run is neither on nor off, so the button
    // says so and refuses both actions instead of inviting a second press at a kill that
    // is already on its way.
    const stopping = !running && !!d.clickerStopping;
    if (toggleBtn) {
        toggleBtn.innerHTML = running ? clickerStopSvg : clickerStartSvg;
        toggleBtn.classList.toggle('is-running', running); // green Start ▷ / red pulsing Stop ▢
        const label = running ? t('stream.stop_clicker') : t('stream.start_clicker');
        toggleBtn.title = label;
        toggleBtn.setAttribute('aria-label', label);
        toggleBtn.disabled = false;
        toggleBtn.style.display = '';
        // Somebody else's run: a plain operator sees the Stop button but cannot press it
        // (the server refuses it too). Start is never gated this way.
        if (running && !canStopClicker(d.clickerStartedBy)) {
            toggleBtn.disabled = true;
            const reason = stopBlockedReason(d.clickerStartedBy);
            toggleBtn.title = reason;
            toggleBtn.setAttribute('aria-label', reason);
        }
        if (stopping) {
            toggleBtn.disabled = true;
            toggleBtn.title = t('stream.clicker_stopping');
            toggleBtn.setAttribute('aria-label', t('stream.clicker_stopping'));
        }
        // The server refuses a start beside a close-shift run; say so instead of a 409 alert.
        if (!running && d.shiftRunning) {
            toggleBtn.disabled = true;
            toggleBtn.title = t('shifts.clicker_blocked');
            toggleBtn.setAttribute('aria-label', t('shifts.clicker_blocked'));
        }
    }
    // Reveal the whole group up front (no wait on the farm-id fetch below).
    if (divider)
        divider.style.display = '';
    if (gearBtn) {
        gearBtn.disabled = false;
        gearBtn.style.display = '';
    }
    if (logsBtn) {
        logsBtn.disabled = false;
        logsBtn.style.display = '';
    }
    if (opsBtn) {
        opsBtn.disabled = false;
        opsBtn.style.display = '';
    }
    if (historyBtn) {
        historyBtn.disabled = false;
        historyBtn.style.display = '';
    }
    if (shiftBtn) {
        // Closing a shift needs the phone to itself, so it is offered only while the clicker is down.
        // A running shift pulses like a running clicker; the button stays live to reach its Stop.
        const busy = running || stopping;
        const shift = !!d.shiftRunning;
        shiftBtn.disabled = busy && !shift;
        shiftBtn.classList.toggle('is-running', shift);
        shiftBtn.title = t(shift ? 'shifts.running_hint' : busy ? 'shifts.close_shift_busy' : 'shifts.close_shift');
        if (shift)
            shiftBtn.style.display = '';
    }
    // The device carries its owning farm's stable id (relay-stamped), so the per-farm
    // clicker endpoints need no name→id lookup — a rename never breaks Start/Stop.
    // Fall back to resolving by name only for an older payload that lacks the id.
    const farmName = d.farmName;
    const farmIdPromise = d.farmId != null
        ? Promise.resolve(d.farmId)
        : (async () => {
            try {
                const fr = await fetch('/api/farms');
                if (fr.ok)
                    return (await fr.json()).find((f) => f.name === farmName)?.id;
            }
            catch { /* farm list unavailable */ }
            return undefined;
        })();
    toggleBtn?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const farmId = await farmIdPromise;
        if (farmId == null)
            return;
        const action = running ? 'stop' : 'start';
        if (action === 'stop' && !canStopClicker(d.clickerStartedBy))
            return;
        if (action === 'stop' && !confirmClickerStop(displayName))
            return;
        toggleBtn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(d.udid)}/${action}`, { method: 'POST' });
            if (!res.ok) {
                // A refused start/stop used to change nothing on screen, which reads exactly
                // like a command that worked — and is the reason an operator presses Stop ten
                // times at a farm that never got the first one. Said out loud, as the duo card
                // already does for the same action.
                const why = (await res.json().catch(() => ({}))).error;
                flashControl(toggleBtn, 'flash-err');
                alert(why || t('farmdev.clicker_action_failed'));
            }
            if (res.ok) {
                // Re-render the card at once with the new run state so the read-only
                // lock, badge and Stop icon apply immediately; the server broadcast
                // later confirms the same state (and reconciles if the launch failed).
                // Pick the matching renderer: a no-video (aggregator / clicker-only)
                // card has no stream, so renderInlinePlayer would paint its "Not
                // connected" placeholder (dropping the clicker control column) — use
                // renderAdbControls there instead.
                const card = toggleBtn.closest('.device-card');
                if (card) {
                    const next = { ...d, clickerRunning: action === 'start' };
                    if (noVideo)
                        renderAdbControls(card, next);
                    else
                        renderInlinePlayer(card, next);
                }
            }
        }
        catch { /* network error — leave state unchanged */ }
        finally {
            toggleBtn.disabled = false;
        }
    });
    gearBtn?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const farmId = await farmIdPromise;
        if (farmId != null)
            void showClickerRestartModal(farmId, d.udid, displayName);
    });
    logsBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        openClickerLogWindow(d.udid);
    });
    opsBtn?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const farmId = await farmIdPromise;
        if (farmId != null)
            openClickerOperationsWindow(farmId, d.udid, displayName);
    });
    historyBtn?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const farmId = await farmIdPromise;
        if (farmId != null)
            openClickerHistoryWindow(farmId, d.udid, displayName);
    });
    // A per-team switch (teams.close_shift), carried on the farm record — shown only once known.
    if (shiftBtn) {
        void farmIdPromise.then(async (farmId) => {
            if (farmId == null)
                return;
            if ((await resolveFarmRecordById(farmId))?.close_shift)
                shiftBtn.style.display = '';
        });
    }
    shiftBtn?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const farmId = await farmIdPromise;
        if (farmId != null)
            openCloseShiftModal(farmId, d.udid);
    });
}
// Guest share links — any farm topology. The button sits under the clicker group
// behind its own divider and opens the links manager (create / copy / revoke).
//
// The gate, mirrored server-side, is can('device.share'); a device with no farm id
// can't be shared — every share route is farm-scoped. Camera passthrough is the one
// VMOS-only part (only the box can inject a camera), so the farm type is passed down
// to the modal rather than used to hide the button.
//
// Aggregator phones need no special case: the guest socket resolves its relay by UDID
// (relay.findRelayForDevice), which lands on the phone's own connection, and the guest
// page decodes that phone's still-frame stream exactly like the panel does. The share is
// a view rather than a remote control there — the phone accepts no INJECT_* control at
// all — which the guest filter allows for; it only ever narrows what may be sent.
function wireShareControl(player, d, displayName) {
    const btn = player.querySelector('.stream-share-btn');
    if (!btn)
        return;
    const farmId = d.farmId;
    if (farmId == null || !can('device.share'))
        return;
    const divider = player.querySelector('.stream-share-divider');
    if (divider)
        divider.style.display = '';
    btn.style.display = '';
    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        showDeviceShareModal(farmId, d.udid, displayName, d.farmType === FarmType.Vmos);
    });
}
// Idle (not-connected) screenshot: there's no live canvas to grab, so fetch a
// one-shot ADB screencap (/screenshot device-query) and write the PNG to the system
// clipboard — same result and green/red flash as the live stream's screenshot button.
function wireIdleScreenshot(btn, d) {
    if (!btn)
        return;
    btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
            const res = await fetch(`/api/devices/${encodeURIComponent(d.udid)}/screenshot`, { cache: 'no-store' });
            if (!res.ok || !('ClipboardItem' in window) || !navigator.clipboard?.write) {
                flashControl(btn, 'flash-err');
                return;
            }
            const blob = await res.blob();
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            flashControl(btn, 'flash-ok');
        }
        catch {
            flashControl(btn, 'flash-err');
        }
        finally {
            btn.disabled = false;
        }
    });
}
// Capture the current decoded frame from the stream canvas and write it to the
// system clipboard as a PNG image. Flashes green on success / red on failure,
// mirroring the clipboard-copy button's feedback.
function wireScreenshot(btn, stream) {
    if (!btn)
        return;
    const flash = (cls) => {
        btn.classList.add(cls);
        setTimeout(() => btn.classList.remove(cls), 900);
    };
    btn.addEventListener('click', () => {
        btn.disabled = true;
        stream.canvas.toBlob((blob) => {
            if (!blob || !('ClipboardItem' in window) || !navigator.clipboard?.write) {
                btn.disabled = false;
                flash('flash-err');
                return;
            }
            navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
                .then(() => flash('flash-ok'))
                .catch(() => flash('flash-err'))
                .finally(() => { btn.disabled = false; });
        }, 'image/png');
    });
}
